import React, { FC, useMemo } from 'react';
import { Box, Tooltip, withStyles, InterestRate, formatAmount, MaterialSymbol } from 'vopay';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { AccountType } from '@/components/Accounts/AccountSummary/AccountSummaryPage';
import styles from './AnnualRatesTooltip.module.scss';

type AnnualRatesTooltipProps = {
  accountType: AccountType;
  balance: number;
  interestRates: InterestRate[];
  customElement: React.JSX.Element;
};

const cn = withStyles(styles);

const AnnualRatesTooltip: FC<AnnualRatesTooltipProps> = ({ accountType, balance, interestRates, customElement }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const annualRates = interestRates.filter((rate) =>
    accountType === 'standard' ? rate.Term === 'short-term' : rate.Term === 'long-term',
  );

  //Filter out when both RangeStart and RangeEnd are invalid
  const filteredRates = useMemo(() => {
    return annualRates.filter(({ RangeStart, RangeEnd }) => {
      const start = parseFloat(RangeStart ?? '0');
      const end = parseFloat(RangeEnd ?? '0');
      return start > 0 || end > 0;
    });
  }, [annualRates]);

  return (
    <Tooltip
      width="600px"
      isDisabled={annualRates.length === 0}
      information={
        <Box gapSize="8px" flx="col" pe="sm" ps="sm" width="550px">
          <Title level="6">
            {t('annualRatesTitle', { accountType: accountType === 'standard' ? 'Standard' : 'Premium' })}
          </Title>
          <p className={cn('ratesInfo')}>{t('ratesInfo')}</p>
          <div>
            <div className={cn('row')}>
              <Box jc="sb" ai="center">
                <span>{t('range')}</span>
                {accountType === 'standard' ? (
                  <span className={cn('ratesTitle')}>{t('annualInterestRate')}</span>
                ) : (
                  <div className={cn('premiumRatesTitle', 'blueBackground', 'blueFont')}>
                    <Box ai="center" gapSize="6px" jc="center">
                      <div>
                        {t('premium', { defaultValue: 'Premium' })}
                        <span>({t('annually')})</span>
                      </div>
                      <MaterialSymbol icon="auto_awesome" />
                    </Box>
                  </div>
                )}
              </Box>
            </div>
            {filteredRates.map((rate) => {
              const isMatching = balance >= parseFloat(rate.RangeStart) && balance <= parseFloat(rate.RangeEnd);
              const isUnlimitedRange = !rate.RangeEnd || parseFloat(rate.RangeEnd) === 0;

              return (
                <div key={rate.RangeStart} className={cn('row', isMatching && 'blueFont')}>
                  <Box jc="sb" ai="center">
                    <Box gapSize="5px">
                      CAD {formatAmount(rate.RangeStart)} {isUnlimitedRange && '+'}
                      {!isUnlimitedRange && ` - CAD ${formatAmount(rate.RangeEnd)}`}
                      {isMatching && <span className={cn('current')}>{t('currentPlan')}</span>}
                    </Box>
                    <span
                      className={cn(
                        'rate',
                        isMatching && 'blueFont',
                        accountType === 'premium' && 'blueBackground',
                        accountType === 'premium' && 'premiumRate',
                        accountType === 'premium' && 'blueFont',
                      )}
                    >
                      {rate.AnnualInterestRate}%
                    </span>
                  </Box>
                </div>
              );
            })}
          </div>
        </Box>
      }
      hasButton={false}
      position={accountType === 'standard' ? 'bottom' : 'top'}
      customElement={customElement}
    />
  );
};

export default AnnualRatesTooltip;
